import { SITE } from "@/site/content";

/**
 * Net Design logo — sharp, simplistic, modern inline SVG.
 * - Geometric N (blue) + angular D (near-black) with a hard center gap.
 * - No rounded corners, no gradients. Pure flat color.
 *
 * variant="icon" -> monogram only (navbar / favicon)
 * variant="full" -> monogram + wordmark
 * variant="wordmark-only" -> just the wordmark
 */
const BLUE = "#1D4ED8";
const INK = "#0F172A";

const Monogram = ({ size = 36 }) => (
  <svg
    width={size}
    height={(size * 40) / 64}
    viewBox="0 0 64 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="block shrink-0"
  >
    {/* N — sharp zigzag */}
    <path
      d="M0 0 H10 L26 26 V0 H36 V40 H26 L10 14 V40 H0 Z"
      fill={BLUE}
    />
    {/* D — angular, chamfered right edge */}
    <path
      d="M40 0 H52 L64 12 V28 L52 40 H40 Z"
      fill={INK}
    />
  </svg>
);

const Wordmark = ({ height = 16, withTagline = false }) => (
  <span className="inline-flex flex-col leading-none">
    <span
      className="font-display font-extrabold tracking-tight"
      style={{ fontSize: height, color: INK, letterSpacing: "-0.02em" }}
    >
      <span style={{ color: BLUE }}>NET</span>
      <span className="mx-[0.18em]" />
      DESIGN
    </span>
    {withTagline && (
      <span
        className="mt-1.5 uppercase text-[10px] tracking-[0.25em] text-gray-500"
        style={{ fontFamily: "Manrope, sans-serif" }}
      >
        Designing your success online
      </span>
    )}
  </span>
);

export const Logo = ({ variant = "full", className = "", height }) => {
  if (variant === "icon") {
    const size = height ?? 40;
    return (
      <span
        data-testid="brand-logo"
        aria-label={`${SITE.brand} logo`}
        className={`inline-flex items-center ${className}`}
      >
        <Monogram size={size} />
      </span>
    );
  }

  if (variant === "wordmark-only") {
    return (
      <span
        data-testid="brand-logo"
        aria-label={SITE.brand}
        className={`inline-flex items-center ${className}`}
      >
        <Wordmark height={height ?? 22} />
      </span>
    );
  }

  // full
  const monoSize = height ? height * 1.6 : 44;
  const wordH = height ?? 22;
  return (
    <span
      data-testid="brand-logo"
      aria-label={SITE.brand}
      className={`inline-flex items-center gap-3 ${className}`}
    >
      <Monogram size={monoSize} />
      <Wordmark height={wordH} withTagline />
    </span>
  );
};
